import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  Fab,
  LinearProgress,
  Pagination,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle,
  Schedule,
  Warning,
  Receipt,
  Add
} from '@mui/icons-material';
import { Invoice, Contract } from '../../types';
import invoiceService from '../../services/invoiceService';
import InvoicesTable from '../InvoicesTable';
import InvoiceMultipleForm from '../forms/InvoiceMultipleForm';
import PaymentForm from '../forms/InvoicePaymentForm';
import RenegociarFaturaModal from '../modals/RenegociarFaturaModal';
import { formatarValor } from '../../utils/formatToView';
import Swal from 'sweetalert2';
//import { InvoiceFormData } from '../../services/invoiceService';
import contractService from '../../services/contractService';

import { useInvoiceActions } from '../../hooks/useInvoiceActions';
import { usePermissions } from '../../contexts/AuthContext';

interface ContractInvoicesProps {
  contractId: number;
  clientId: number;
  contractStatus: 'ativo' | 'bloqueado' | 'cancelado' | 'aguardando_instalacao' | 'aguardando_assinatura' | 'suspenso';
}

const ContractInvoices: React.FC<ContractInvoicesProps> = ({ contractId, clientId, contractStatus }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  //const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('todos');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    paymentMethod: '',
    transactionId: ''
  });
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);
  const [newPaymentDialogOpen, setNewPaymentDialogOpen] = useState(false);
  const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState<Invoice | null>(null);

  // Estados para impressão em massa
  const [selectedInvoiceForPrint, setSelectedInvoiceForPrint] = useState<Invoice | null>(null);

  const { can } = usePermissions();
  const podeDeletarFatura = can('faturas.deletar');

  // Estados para modal de renegociação
  const [renegociarModalOpen, setRenegociarModalOpen] = useState(false);
  const [renegociarIds, setRenegociarIds] = useState<number[]>([]);
  const [deleteCancelledMode, setDeleteCancelledMode] = useState(false);




  useEffect(() => {
    loadInvoices();
  }, [contractId, page]);

  // Buscar informações do contrato para obter cliente_id
  /* useEffect(() => {
     const loadContract = async () => {
       try {
         const response = await contractService.getById(contractId);
         if (response.success && response.data) {
           //setContract(response.data);
         }
       } catch (error) {
         console.error('Erro ao carregar contrato:', error);
       }
     };
 
     loadContract();
   }, [contractId]);
 */
  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar faturas do contrato específico
      const response = await invoiceService.getInvoicesByContract(contractId);

      if (response.success) {
        // Garantir que data é um array, mesmo se for undefined ou null
        const invoicesData = Array.isArray(response.data) ? response.data : [];
        // Ordenar por ID decrescente (mais recentes primeiro)
        const faturasOrdenadas = invoicesData.sort((a, b) => b.id - a.id);
        setInvoices(faturasOrdenadas);
        // Para paginação simples, assumindo que todas as faturas são carregadas de uma vez
        setTotalPages(1);
      } else {
        const errorMessage = response.message || 'Erro ao carregar faturas do contrato';
        console.error('Erro na resposta da API:', errorMessage);
        setError(errorMessage);
        setInvoices([]); // Garantir que invoices seja um array vazio em caso de erro
      }
    } catch (err: any) {
      console.error('Erro ao carregar faturas:', err);

      let errorMessage = 'Erro ao carregar faturas do contrato';

      if (err.response) {
        // Erro da API
        console.error('Erro da API:', err.response.status, err.response.data);
        errorMessage = err.response.data?.message || `Erro ${err.response.status}: ${err.response.statusText}`;
      } else if (err.request) {
        // Erro de rede
        console.error('Erro de rede:', err.request);
        errorMessage = 'Erro de conexão com o servidor. Verifique se o backend está rodando.';
      } else {
        // Outro tipo de erro
        console.error('Erro desconhecido:', err.message);
        errorMessage = err.message || 'Erro desconhecido';
      }

      setError(errorMessage);
      setInvoices([]); // Garantir que invoices seja um array vazio em caso de erro
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type: 'success' | 'error' | 'warning', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  // Hook centralizado para ações das faturas
  const {
    formOpen,
    selectedInvoice,
    bulkEditMode,
    selectedInvoices,
    selectedInvoicesData,
    handleCancelBulkMode,
    handleSelectInvoice,
    handleMassCancel,
    handleMassPrint,
    handleMassEdit,
    handleMassDelete,
    setFormOpen,
    setSelectedInvoice,
    hasSelectedInvoices
  } = useInvoiceActions({
    showAlert,
    loadInvoices
  });

  const handleDeleteCancelledClick = async () => {
    if (!deleteCancelledMode) {
      handleCancelBulkMode();
      if (statusFilter === 'ocultar_cancelados') {
        setStatusFilter('todos');
      }
      setDeleteCancelledMode(true);
      return;
    }

    if (hasSelectedInvoices) {
      await handleMassDelete(selectedInvoices);
      setDeleteCancelledMode(false);
      return;
    }

    setDeleteCancelledMode(false);
  };

  const handleMarkAsPaid = async () => {
    if (!selectedPaymentInvoice) return;

    try {
      const response = await invoiceService.markAsPaid(
        selectedPaymentInvoice.id,
        paymentData.paymentMethod,
        paymentData.transactionId || undefined
      );

      if (response.success) {
        showAlert('success', 'Fatura marcada como paga!');
        loadInvoices(); // Recarregar lista
        setPaymentDialogOpen(false);
        setPaymentData({ paymentMethod: '', transactionId: '' });
      }
    } catch (error: any) {
      showAlert('error', error.message || 'Erro ao marcar fatura como paga');
    }
  };

  /*const handleDownloadInvoice = () => {
    console.log('Download fatura');
    // TODO: Implementar lógica para download de PDF
    showAlert('success', 'Funcionalidade de download será implementada em breve');
  };*/

  const handleCreateInvoice = () => {
    setSelectedInvoice(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    // Desativar modo de edição em massa quando o formulário for fechado
    if (bulkEditMode) {
      // handleCancelBulkMode();
    }
  };

  const handleSaveInvoice = async () => {
    setFormOpen(false);
    // Aguardar um momento para garantir que as faturas foram persistidas no BD
    setTimeout(() => {
      loadInvoices(); // Recarregar faturas do BD
    }, 500);
  };

  const handleReceivePaymentClick = (invoice: Invoice) => {
    setSelectedPaymentInvoice(invoice);
    setNewPaymentDialogOpen(true);
  };

  // Handlers para impressão em massa
  const handleMassPrintClick = async () => {
    try {
      const printType = await handleMassPrint();

      if (printType === 'fatura' || printType === 'carne' || printType === 'capa_carne') {
        if (selectedInvoices.length > 0) {
          // Mostrar alert de carregamento
          Swal.fire({
            title: 'Gerando PDF...',
            text: 'Aguarde enquanto geramos o documento',
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });

          const blob = await invoiceService.imprimirPDF(selectedInvoices, printType as 'fatura' | 'carne' | 'capa_carne');
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          Swal.close();
        } else {
          showAlert('warning', 'Nenhuma fatura selecionada');
        }
      }
    } catch (error) {
      console.error('Erro ao processar impressão em massa:', error);
      showAlert('error', 'Erro ao gerar PDF das faturas');
      Swal.close();
    }
  };

  const calculateTotal = (invoice: Invoice) => {
    // Garantir que os valores sejam números válidos
    const valor = Number(invoice.valor) || 0;
    const acrescimo = Number(invoice.acrescimo) || 0;
    const desconto = Number(invoice.desconto) || 0;

    return invoiceService.calculateTotal(valor, acrescimo, desconto);
  };

  if (loading) {
    return (
      <Box>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Carregando mensalidades...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Alert */}
      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight="bold">
          Mensalidades do Contrato
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="status-filter-label" sx={{ fontSize: '0.8rem' }}>Status</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
              sx={{ height: 30, fontSize: '0.8rem' }}
            >
              <MenuItem dense value="todos">Todos</MenuItem>
              <MenuItem dense value="ocultar_cancelados">Ocultar Cancelados</MenuItem>
              <MenuItem dense value="aberto">Aberto</MenuItem>
              <MenuItem dense value="pago">Pago</MenuItem>
              <MenuItem dense value="vencido">Vencido</MenuItem>
              <MenuItem dense value="parcialmente_pago">Parcialmente Pago</MenuItem>
              <MenuItem dense value="cancelado">Cancelado</MenuItem>
              <MenuItem dense value="premiado">Premiado</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            color="error"
            disabled={deleteCancelledMode || !hasSelectedInvoices}
            onClick={() => handleMassCancel(selectedInvoices)}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            Cancelar {hasSelectedInvoices && `(${selectedInvoices.length})`}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={deleteCancelledMode || !hasSelectedInvoices}
            onClick={handleMassPrintClick}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            Imprimir {hasSelectedInvoices && `(${selectedInvoices.length})`}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            disabled={deleteCancelledMode || !hasSelectedInvoices}
            onClick={handleMassEdit}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            Editar {hasSelectedInvoices && `(${selectedInvoices.length})`}
          </Button>
          <Button
            variant="outlined"
            color="info"
            disabled={deleteCancelledMode || !hasSelectedInvoices}
            onClick={() => {
              setRenegociarIds(selectedInvoices);
              setRenegociarModalOpen(true);
            }}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            Renegociar {hasSelectedInvoices && `(${selectedInvoices.length})`}
          </Button>
          {podeDeletarFatura && (
            <Button
              variant={deleteCancelledMode && hasSelectedInvoices ? 'contained' : 'outlined'}
              color="error"
              onClick={handleDeleteCancelledClick}
              sx={{ display: { xs: 'none', sm: 'flex' } }}
            >
              {deleteCancelledMode && hasSelectedInvoices
                ? `Deletar (${selectedInvoices.length})`
                : 'Deletar Canceladas'}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateInvoice}
            disabled={contractStatus === 'cancelado'}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            Nova Fatura
          </Button>
        </Box>
      </Box>

      {/* Resumo */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Faturas Pagas
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {invoices.filter(inv => inv.status === 'pago').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Schedule color="warning" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Abertas
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {invoices.filter(inv => inv.status === 'aberto').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Warning color="error" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Vencidas
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {invoices.filter(inv => inv.status === 'vencido').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Receipt color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Arrecadado
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {formatarValor(
                      invoices
                        .filter(inv => inv.status === 'pago')
                        .reduce((sum, inv) => sum + calculateTotal(inv), 0)
                    )}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela de Faturas usando o componente reutilizável */}
      <InvoicesTable
        invoices={
          statusFilter === 'todos'
            ? invoices
            : statusFilter === 'ocultar_cancelados'
              ? invoices.filter(f => f.status !== 'cancelado')
              : invoices.filter(f => f.status === statusFilter)
        }
        error={error}
        loading={loading}
        showContractColumn={false}
        showGatewayColumn={true}
        showClientColumn={false}
        onReceivePayment={handleReceivePaymentClick}
        onInvoiceUpdated={loadInvoices}
        showAlert={(type, message) => setAlert({ type, message })}
        onSelectInvoice={handleSelectInvoice}
        selectedInvoices={selectedInvoices}
        selectionMode={deleteCancelledMode ? 'delete_cancelled' : 'default'}
        onRenegociar={(ids) => {
          setRenegociarIds(ids);
          setRenegociarModalOpen(true);
        }}
      />

      {/* Paginação */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}

      {/* FAB para mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 86, // Footer height (70px) + spacing (16px) = 86px
            right: 16,
            zIndex: theme.zIndex.appBar - 1, // Abaixo do FAB do dashboard mas acima do footer
          }}
          onClick={handleCreateInvoice}
        >
          <Add />
        </Fab>
      )}

      {/* Dialog de Pagamento Simples */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)}>
        <DialogTitle>Marcar Fatura como Paga</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Método de Pagamento</InputLabel>
              <Select
                value={paymentData.paymentMethod}
                onChange={(e) => setPaymentData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                label="Método de Pagamento"
              >
                <MenuItem value="cartao_credito">Cartão de Crédito</MenuItem>
                <MenuItem value="boleto">Boleto</MenuItem>
                <MenuItem value="pix">PIX</MenuItem>
                <MenuItem value="dinheiro">Dinheiro</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="ID da Transação (opcional)"
              fullWidth
              value={paymentData.transactionId}
              onChange={(e) => setPaymentData(prev => ({ ...prev, transactionId: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleMarkAsPaid}
            variant="contained"
            disabled={!paymentData.paymentMethod}
          >
            Confirmar Pagamento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Recebimento de Pagamento */}
      <InvoiceMultipleForm
        open={formOpen}
        onClose={handleCloseForm}
        invoice={selectedInvoice}
        onSubmit={handleSaveInvoice}
        title={bulkEditMode ? `Editar ${selectedInvoices.length} Fatura(s)` : "Nova Fatura"}
        clientId={clientId}
        contractId={contractId}
        isEditMode={bulkEditMode}
        selectedInvoicesData={bulkEditMode ? selectedInvoicesData : undefined}
      />

      <PaymentForm
        open={newPaymentDialogOpen}
        onClose={() => {
          setNewPaymentDialogOpen(false);
          setSelectedPaymentInvoice(null);
        }}
        invoice={selectedPaymentInvoice}
        onSuccess={(paymentData, invoice) => {
          console.log('✅ Pagamento processado com sucesso:', { paymentData, invoice });
          setNewPaymentDialogOpen(false);
          setSelectedPaymentInvoice(null);
          loadInvoices();
        }}
        onError={(error) => {
          console.error('❌ Erro no pagamento:', error);
        }}
        onCancel={() => {
          console.log('🚫 Pagamento cancelado pelo usuário');
        }}
        reloadDataOnSuccess={loadInvoices}
        autoCloseOnSuccess={true}
      />

      {/* Modal de Renegociação */}
      <RenegociarFaturaModal
        open={renegociarModalOpen}
        onClose={() => {
          setRenegociarModalOpen(false);
          setRenegociarIds([]);
        }}
        invoiceIds={renegociarIds}
        onSuccess={() => {
          loadInvoices();
          showAlert('success', 'Renegociação realizada com sucesso!');
        }}
        onError={(msg) => showAlert('error', msg)}
      />

      <Snackbar
        open={!!alert}
        autoHideDuration={6000}
        onClose={() => setAlert(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setAlert(null)}
          severity={alert?.type}
          sx={{ width: '100%' }}
        >
          {alert?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContractInvoices; 